/**
 * Sentry Error Tracking — Last Nad Standing
 *
 * Only initializes if VITE_SENTRY_DSN is set in .env.
 * Free tier: 5K events/month — sufficient for early-stage monitoring.
 *
 * Setup steps:
 * 1. Create account at https://sentry.io (free)
 * 2. Create a React project → copy DSN
 * 3. Add to frontend/.env: VITE_SENTRY_DSN=https://xxx@yyy.ingest.sentry.io/zzz
 */
import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

export function initSentry() {
  if (!SENTRY_DSN) {
    console.info('[Sentry] No DSN configured — error tracking disabled. Set VITE_SENTRY_DSN in .env to enable.')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE || 'production',

    // Performance: sample 10% of transactions (adjust as traffic grows)
    tracesSampleRate: 0.1,

    // Only send errors from our domain, not third-party scripts
    allowUrls: [
      /https?:\/\/(www\.)?lastnad\.com/,
      /https?:\/\/localhost/,
    ],

    // Filter noisy errors that aren't actionable
    beforeSend(event) {
      const msg = event?.exception?.values?.[0]?.value || ''
      // Ignore MetaMask/WalletConnect user rejection (not a bug)
      if (msg.includes('user rejected') || msg.includes('User denied')) return null
      // Ignore network errors from public RPC (expected under load)
      if (msg.includes('TIMEOUT') || msg.includes('429') || msg.includes('rate limit')) return null
      return event
    },

    // Tag with chain info for easier debugging
    initialScope: {
      tags: { app: 'last-nad-standing', version: '2.0' },
    },
  })

  console.info('[Sentry] Error tracking initialized')
}

// Re-export for use as error boundary
export { Sentry }
